import { BRAND } from "../brand";

/** Notifications, announcements, knowledge base and "Show gaps" texts (English). Missing keys fall back to Turkish. */
export const comms = {
  kinds: {
    leave_submitted: "Leave requests waiting for your approval",
    leave_approved: "When your leave is approved",
    leave_rejected: "When your leave request is declined",
    leave_returned: "When your leave request is sent back",
    leave_cancelled: "Leave cancellations in your team",
    leave_step_approved: "When your leave request moves forward",
    approval_rerouted: "Approvals handed over to you",
    approval_reminder: "Reminders for overdue approvals",
    delegation_started: "When someone delegates to you",
    document_requested: "When a document is requested from you",
    document_reviewed_accepted: "When your document is accepted",
    document_reviewed_rejected: "When your document needs re-uploading",
    payslip_published: "When your payslip is published",
    task_assigned: "When a task is assigned to you",
    checklist_completed: "When an onboarding/offboarding list is done",
    expense_submitted: "Expenses waiting for your approval",
    expense_approved: "When your expense is approved",
    expense_rejected: "When your expense is declined",
    expense_returned: "When your expense is sent back",
    expense_paid: "When your expense is paid",
    advance_submitted: "Advances waiting for your approval",
    advance_approved: "When your advance is approved",
    advance_rejected: "When your advance is declined",
    advance_paid: "When your advance is paid",
    announcement: "New announcements",
    asset_assigned: "When an asset is assigned to you",
    support_session_started: `When ${BRAND.name} support opens a session`,
  },
  push: {
    title: BRAND.name,
    generic: {
      approval_request: "A request is waiting for your approval.",
      leave_update: "There is an update on your leave request.",
      team_leave_cancelled: "A leave request in your team was cancelled.",
      delegation: "Approvals have been delegated to you.",
      document_requested: "A document has been requested from you.",
      document_update: "There is an update on your document.",
      payroll: "You have a new payslip.",
      task: "A new task has been assigned to you.",
      checklist_done: "A process you follow has been completed.",
      money_update: "There is an update on your expense or advance request.",
      announcement: "There is a new announcement.",
      asset: "New equipment has been assigned to you.",
      support: `${BRAND.name} support opened a read-only session in your company account.`,
      other: "You have a new notification.",
    },
  },
  kindGroups: {
    leave: "Leave",
    approvals: "Approvals and delegation",
    documents: "Documents",
    payroll: "Payroll",
    tasks: "Tasks",
    expenses: "Expenses and advances",
    announcements: "Announcements",
    assets: "Assets",
    security: "Account security",
  },
  announcements: {
    audience: { all: "Everyone", branches: "Branches: {names}", departments: "Departments: {names}" },
  },
  knowledge: {
    noResults: "No policy was found on this topic; you can ask your HR team.",
  },
  issues: {
    labels: {
      missing_manager: "No manager",
      missing_department: "No department",
      missing_national_id: "No Turkish ID number",
      missing_iban: "No IBAN",
      missing_emergency_contact: "No emergency contact",
      missing_required_document: "Required document missing",
      expired_document: "Document expired",
      missing_custom_field: "Required field empty",
      no_account: "No account",
      pending_invitation: "Invitation pending",
      missing_opening_balance: "No leave opening balance",
    },
    detail: {
      missing_manager: "No manager assigned; leave approvals have no one to go to.",
      missing_department: "Not assigned to a department.",
      missing_national_id: "Turkish ID number not entered.",
      missing_iban: "No IBAN entered for payments.",
      missing_emergency_contact: "No emergency contact entered.",
      missing_custom_field: "Required field is empty: {label}.",
      no_account: "No app account and no invitation sent.",
      pending_invitation: "Invited on {date}; not accepted yet.",
      pending_invitation_expired: "The invitation expired on {date}; send a new one.",
      missing_opening_balance: "{years} years of service but no annual leave opening balance; the balance will look too low.",
    },
    notes: {
      topPerson: "{name} is the only person without a manager who leads a team, so they are treated as the top of the company and not listed under \"No manager\".",
      executives: "People with the executive role may have no manager; they are not listed under \"No manager\".",
      multipleRoots: "{count} team leads have no manager; the top person can't be determined, so all of them are listed.",
    },
  },
  summary: {
    roleTitleDept: "{name} works in {department} as {title}.",
    roleDept: "{name} works in {department}.",
    roleTitle: "{name} works as {title}.",
    roleNone: "No department or title entered for {name}.",
    branch: "Branch: {branch}.",
    manager: "Manager: {manager}.",
    noManager: "No manager assigned.",
    seniority: "Service: {duration} (since {date}).",
    seniorityNew: "Service: less than a month (since {date}).",
    startsOn: "Start date: {date}.",
    status: { preboarding: "Has not started yet.", offboarding: "Leaving process in progress.", terminated: "Has left the company." },
    years_one: "{count} year",
    years_other: "{count} years",
    months_one: "{count} month",
    months_other: "{count} months",
    annualLeave: "Annual leave: {available} available.",
    annualLeavePending: "Annual leave: {available} available ({pending} pending approval).",
    noPending: "No pending requests.",
    pending: "Pending requests: {list}.",
    pendingLeave_one: "{count} leave request",
    pendingLeave_other: "{count} leave requests",
    pendingExpense_one: "{count} expense",
    pendingExpense_other: "{count} expenses",
    pendingAdvance_one: "{count} advance request",
    pendingAdvance_other: "{count} advance requests",
    noIssues: "No open gaps.",
    issues: "Open gaps: {list}.",
  },
};

/** English notification texts (Turkish catalog: tr/notifications.ts). */
export const notificationsEn = {
  leave_submitted: { title: "{name} requested leave", body: "{type}, {range} ({amount}). Waiting for your approval." },
  leave_approved: { title: "Your leave was approved", body: "{type}, {range}. Enjoy your time off." },
  leave_rejected: { title: "Your leave request was declined", body: "{type}, {range}. Note: {comment}" },
  leave_returned: { title: "Your leave request was sent back for changes", body: "{type}, {range}. Note: {comment}" },
  leave_cancelled: { title: "{name} cancelled a leave request", body: "{type}, {range}." },
  leave_step_approved: { title: "Your leave request moved forward", body: "{name} approved. Next: {next}." },
  approval_rerouted: { title: "An approval was handed over to you", body: "{name}'s request is now waiting for your approval." },
  approval_reminder: { title: "{name} has been waiting {days} days for your answer", body: "Open the request to approve, ask for changes or decline." },
  delegation_started: { title: "Delegation granted", body: "{name} delegated their approvals to you for {range}." },
  announcement: { title: "Announcement: {title}", body: "{excerpt}" },
  support_session_started: {
    title: `${BRAND.name} support session started`,
    body: `${BRAND.name} support is viewing your company account read-only. Details: Settings → ${BRAND.name} support access.`,
  },
};
